import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaChartBar, FaListAlt, FaTrophy, FaClock, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Stats from './Stats.js';
import QNA from './QNA.js';
import { useTheme } from '../../context/ThemeContext';

const Result = ({
  totalQuestions,
  correctAnswers,
  timeTaken,
  questionsAndAnswers,
  replayQuiz,
  resetQuiz,
}) => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('Stats');

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };

  return (
    <div className={`min-h-screen py-6 sm:py-8 lg:py-12 transition-colors duration-300 ${isDark
        ? 'bg-dark-primary'
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with celebration */}
        <motion.div
          className="text-center mb-6 sm:mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div
            className="inline-block mb-4"
            animate={{
              y: [0, -10, 0],
              rotate: [0, 5, -5, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 1
            }}
          >
            <FaTrophy className="text-4xl sm:text-5xl md:text-6xl text-yellow-400" />
          </motion.div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
            Quiz Completed!
          </h1>
          <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Great job completing the quiz! Check your results below.
          </p>
        </motion.div>

        {/* Modern Tab Navigation */}
        <motion.div
          className={`rounded-2xl overflow-hidden mb-6 sm:mb-8 ${isDark
              ? 'bg-dark-secondary/80 border border-white/5'
              : 'bg-white shadow-xl'
            }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className={`flex ${isDark ? 'border-b border-white/5' : 'border-b border-gray-200'}`}>
            <button
              onClick={() => handleTabClick('Stats')}
              className={`flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 sm:py-5 text-sm sm:text-base font-semibold transition-all duration-300 relative ${activeTab === 'Stats'
                  ? isDark
                    ? 'text-purple-400 bg-purple-500/10'
                    : 'text-purple-600 bg-gradient-to-b from-purple-50 to-pink-50'
                  : isDark
                    ? 'text-gray-400 hover:text-purple-400 hover:bg-white/5'
                    : 'text-gray-600 hover:text-purple-600 hover:bg-gray-50'
                }`}
            >
              <FaChartBar className="text-lg sm:text-xl" />
              <span className="hidden sm:inline">Statistics</span>
              <span className="sm:hidden">Stats</span>
              {activeTab === 'Stats' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 to-pink-600"
                  layoutId="activeTab"
                />
              )}
            </button>
            <button
              onClick={() => handleTabClick('QNA')}
              className={`flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 sm:py-5 text-sm sm:text-base font-semibold transition-all duration-300 relative ${activeTab === 'QNA'
                  ? isDark
                    ? 'text-purple-400 bg-purple-500/10'
                    : 'text-purple-600 bg-gradient-to-b from-purple-50 to-pink-50'
                  : isDark
                    ? 'text-gray-400 hover:text-purple-400 hover:bg-white/5'
                    : 'text-gray-600 hover:text-purple-600 hover:bg-gray-50'
                }`}
            >
              <FaListAlt className="text-lg sm:text-xl" />
              <span>Review Answers</span>
              {activeTab === 'QNA' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 to-pink-600"
                  layoutId="activeTab"
                />
              )}
            </button>
          </div>

          {/* Tab Content with smooth transitions */}
          <div className="relative min-h-[400px] sm:min-h-[500px]">
            <div
              className={`transition-all duration-500 ease-in-out ${activeTab === 'Stats' ? 'opacity-100 translate-x-0' : 'opacity-0 absolute translate-x-4 pointer-events-none'
                }`}
            >
              <Stats
                totalQuestions={totalQuestions}
                correctAnswers={correctAnswers}
                timeTaken={timeTaken}
                replayQuiz={replayQuiz}
                resetQuiz={resetQuiz}
                isDark={isDark}
              />
            </div>
            <div
              className={`transition-all duration-500 ease-in-out ${activeTab === 'QNA' ? 'opacity-100 translate-x-0' : 'opacity-0 absolute translate-x-4 pointer-events-none'
                }`}
            >
              <QNA questionsAndAnswers={questionsAndAnswers} isDark={isDark} />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

Result.propTypes = {
  totalQuestions: PropTypes.number.isRequired,
  correctAnswers: PropTypes.number.isRequired,
  timeTaken: PropTypes.number.isRequired,
  questionsAndAnswers: PropTypes.array.isRequired,
  replayQuiz: PropTypes.func.isRequired,
  resetQuiz: PropTypes.func.isRequired,
};

export default Result;
